import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Warehouse, Search, X, Download, Loader2 } from 'lucide-react';
import { useTheme } from '../core/ui/ThemeContext';
import { useLanguage } from '../core/i18n/LanguageContext';
import { getLagerWerke, getLagerFilterOptions, getLagerMaterials } from 'core/api/apiService';
import MaterialFilters from './lager/MaterialFilters';
import MaterialTable from './lager/MaterialTable';
import SavedFilters from './lager/SavedFilters';
import AutoFilterToggle from './lager/AutoFilterToggle';
import WerkDropdown from './lager/WerkDropdown';
import useLagerExport from './lager/hooks/useLagerExport';

/**
 * LagerView — Materialsuche & Uebersicht (Phase 1–3c + Dashboard-v2).
 * Props:
 *   user                    Object  (aus useAuth)
 *   onBack                  () => void   optional (Sidebar uebernimmt Navigation)
 *   showSuccess             (msg) => void
 *   showError               (msg) => void
 *   onCartUpdated           () => void
 *   onNavigateToCreateBanf  (prefill) => void
 *   initialWerk             string   geteiltes Werk von LagerShell
 *   onWerkChange            (werk: string) => void
 *   initialFilter           object|null  Drill-down-Filter von KPI-Karte
 *   onFilterApplied         () => void   Signalisiert LagerShell: pendingFilter angewendet
 */
const LagerView = ({
  user,
  showSuccess,
  showError,
  onCartUpdated,
  onNavigateToCreateBanf,
  initialWerk,
  onWerkChange,
  initialFilter,
  onFilterApplied,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  // ---------- Werke ----------
  const [werke, setWerke] = useState([]);
  const [selectedWerk, setSelectedWerk] = useState(initialWerk || '');
  const [werkOpen, setWerkOpen] = useState(false);
  const werkRef = useRef(null);

  // ---------- Filter-Optionen ----------
  const [filterOptions, setFilterOptions] = useState({
    warengruppen: [], materialarten: [], lagerorte: [],
    beschaffungsarten: [], dispomerkmale: [],
    einkaeufergruppen: [], sonderbestand_lieferanten: [],
  });

  // ---------- Schnellfilter ----------
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedWg, setSelectedWg] = useState([]);
  const [selectedMa, setSelectedMa] = useState([]);
  const [bestandsstatus, setBestandsstatus] = useState('alle');

  // ---------- Erweiterte Filter ----------
  const DEFAULT_ADV = {
    lagerort: [], beschaffungsart: [], dispomerkmal: [],
    bestand_min: '', bestand_max: '',
    offene_banf: 'alle', offene_bestellung: 'alle',
    handlungsbedarf: false,
    stock_type: [], vendor: [], ekgrp: [],
    sperrbestand: false, qmbestand: false,
  };
  const [advFilters, setAdvFilters] = useState(DEFAULT_ADV);
  const [debouncedBestandMin, setDebouncedBestandMin] = useState('');
  const [debouncedBestandMax, setDebouncedBestandMax] = useState('');

  // ---------- Drill-down: kritisch ----------
  const [kritischFilter, setKritischFilter] = useState(false);

  // ---------- Klassifizierungs-Merkmals-Filter ----------
  const [merkmale, setMerkmale] = useState([]);
  const [debouncedMerkmale, setDebouncedMerkmale] = useState([]);

  // ---------- Sortierung / Paginierung ----------
  const [sort, setSort] = useState('matnr:asc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  // ---------- Ergebnisse ----------
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [werkeLoading, setWerkeLoading] = useState(true);

  // ---------- Export ----------
  const { exportLoading, handleExport } = useLagerExport(
    user,
    {
      selectedWerk, debouncedSearch, selectedWg, selectedMa, bestandsstatus,
      advFilters, debouncedBestandMin, debouncedBestandMax,
      kritischFilter, debouncedMerkmale, sort,
    },
    showError,
  );

  // ---------- initialFilter von Shell anwenden ----------
  useEffect(() => {
    if (!initialFilter) return;
    // kritisch-Drill-down
    if (initialFilter.kritisch === true) {
      setKritischFilter(true);
    } else {
      setKritischFilter(false);
    }
    // offene_banf-Drill-down
    if (initialFilter.offene_banf) {
      setAdvFilters((prev) => ({ ...prev, offene_banf: initialFilter.offene_banf }));
    }
    setPage(1);
    if (typeof onFilterApplied === 'function') onFilterApplied();
  }, [initialFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- initialWerk synchronisieren (Shell wechselt Werk) ----------
  useEffect(() => {
    if (initialWerk && initialWerk !== selectedWerk) {
      setSelectedWerk(initialWerk);
    }
  }, [initialWerk]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Werke laden ----------
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setWerkeLoading(true);
      try {
        const data = await getLagerWerke(user);
        if (cancelled) return;
        const list = Array.isArray(data?.werke) ? data.werke : [];
        setWerke(list);
        // Wenn Shell noch kein Werk hat, Default setzen
        if (!selectedWerk) {
          const def = data?.default_werk || (list.length > 0 ? list[0].werk : '');
          setSelectedWerk(def);
          if (typeof onWerkChange === 'function') onWerkChange(def);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || t('lm.error'));
      } finally {
        if (!cancelled) setWerkeLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Filter-Optionen wenn Werk wechselt ----------
  useEffect(() => {
    if (!selectedWerk) return;
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getLagerFilterOptions(user, selectedWerk);
        if (cancelled) return;
        setFilterOptions({
          warengruppen: Array.isArray(data?.warengruppen) ? data.warengruppen : [],
          materialarten: Array.isArray(data?.materialarten) ? data.materialarten : [],
          lagerorte: Array.isArray(data?.lagerorte) ? data.lagerorte : [],
          beschaffungsarten: Array.isArray(data?.beschaffungsarten) ? data.beschaffungsarten : [],
          dispomerkmale: Array.isArray(data?.dispomerkmale) ? data.dispomerkmale : [],
          einkaeufergruppen: Array.isArray(data?.einkaeufergruppen) ? data.einkaeufergruppen : [],
          sonderbestand_lieferanten: Array.isArray(data?.sonderbestand_lieferanten) ? data.sonderbestand_lieferanten : [],
        });
      } catch { /* nicht kritisch */ }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedWerk, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Debounce Suche ----------
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // ---------- Debounce Bestand-Bereich ----------
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedBestandMin(advFilters.bestand_min); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [advFilters.bestand_min]);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedBestandMax(advFilters.bestand_max); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [advFilters.bestand_max]);

  // ---------- Debounce Merkmale ----------
  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedMerkmale(merkmale); setPage(1); }, 400);
    return () => clearTimeout(timer);
  }, [JSON.stringify(merkmale)]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Materialien laden ----------
  const loadMaterials = useCallback(async () => {
    if (!selectedWerk) return;
    setLoading(true);
    setError(null);
    try {
      const params = {
        werk: selectedWerk,
        search: debouncedSearch || undefined,
        warengruppe: selectedWg.length > 0 ? selectedWg.join(',') : undefined,
        materialart: selectedMa.length > 0 ? selectedMa.join(',') : undefined,
        bestandsstatus: bestandsstatus !== 'alle' ? bestandsstatus : undefined,
        lagerort: Array.isArray(advFilters.lagerort) && advFilters.lagerort.length > 0 ? advFilters.lagerort.join(',') : undefined,
        beschaffungsart: Array.isArray(advFilters.beschaffungsart) && advFilters.beschaffungsart.length > 0 ? advFilters.beschaffungsart.join(',') : undefined,
        dispomerkmal: Array.isArray(advFilters.dispomerkmal) && advFilters.dispomerkmal.length > 0 ? advFilters.dispomerkmal.join(',') : undefined,
        bestand_min: debouncedBestandMin !== '' ? debouncedBestandMin : undefined,
        bestand_max: debouncedBestandMax !== '' ? debouncedBestandMax : undefined,
        offene_banf: advFilters.offene_banf !== 'alle' ? advFilters.offene_banf : undefined,
        offene_bestellung: advFilters.offene_bestellung !== 'alle' ? advFilters.offene_bestellung : undefined,
        handlungsbedarf: advFilters.handlungsbedarf === true ? true : undefined,
        stock_type: Array.isArray(advFilters.stock_type) && advFilters.stock_type.length > 0 ? advFilters.stock_type.join(',') : undefined,
        vendor: Array.isArray(advFilters.vendor) && advFilters.vendor.length > 0 ? advFilters.vendor.join(',') : undefined,
        ekgrp: Array.isArray(advFilters.ekgrp) && advFilters.ekgrp.length > 0 ? advFilters.ekgrp.join(',') : undefined,
        sperrbestand: advFilters.sperrbestand === true ? true : undefined,
        qmbestand: advFilters.qmbestand === true ? true : undefined,
        kritisch: kritischFilter === true ? true : undefined,
        merkmale: Array.isArray(debouncedMerkmale) && debouncedMerkmale.length > 0 ? debouncedMerkmale : undefined,
        sort, page, per_page: perPage,
      };
      const data = await getLagerMaterials(user, params);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(data?.total ?? 0);
      setTotalPages(data?.total_pages ?? 1);
    } catch (err) {
      setError(err.message || t('lm.error'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    selectedWerk, debouncedSearch, selectedWg, selectedMa, bestandsstatus,
    advFilters.lagerort, advFilters.beschaffungsart, advFilters.dispomerkmal,
    advFilters.offene_banf, advFilters.offene_bestellung, advFilters.handlungsbedarf,
    advFilters.stock_type, advFilters.vendor, advFilters.ekgrp, advFilters.sperrbestand, advFilters.qmbestand,
    debouncedBestandMin, debouncedBestandMax,
    kritischFilter, debouncedMerkmale, sort, page, perPage, user,
  ]);

  useEffect(() => { loadMaterials(); }, [loadMaterials]);

  // ---------- Werke-Dropdown schliessen ----------
  useEffect(() => {
    const handle = (e) => {
      if (werkRef.current && !werkRef.current.contains(e.target)) setWerkOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // ---------- Handler ----------
  const handleSort = (colKey) => {
    const [cur, dir] = sort.split(':');
    const newDir = cur === colKey && dir === 'asc' ? 'desc' : 'asc';
    setSort(`${colKey}:${newDir}`);
    setPage(1);
  };

  const handleResetFilters = () => {
    setSelectedWg([]);
    setSelectedMa([]);
    setBestandsstatus('alle');
    setSearch('');
    setAdvFilters(DEFAULT_ADV);
    setMerkmale([]);
    setKritischFilter(false);
    setPage(1);
  };

  const handlePerPageChange = (n) => { setPerPage(n); setPage(1); };

  const handleWerkChange = (werk) => {
    setSelectedWerk(werk);
    setWerkOpen(false);
    setPage(1);
    setSelectedWg([]);
    setSelectedMa([]);
    setAdvFilters(DEFAULT_ADV);
    setMerkmale([]);
    setKritischFilter(false);
    if (typeof onWerkChange === 'function') onWerkChange(werk);
  };

  const handleAdvChange = (patch) => {
    setAdvFilters((prev) => ({ ...prev, ...patch }));
    const isNumberField = 'bestand_min' in patch || 'bestand_max' in patch;
    if (!isNumberField) setPage(1);
  };

  const handleApplySavedFilter = (filterPatch) => {
    if (filterPatch.werk && filterPatch.werk !== selectedWerk) {
      setSelectedWerk(filterPatch.werk);
      if (typeof onWerkChange === 'function') onWerkChange(filterPatch.werk);
    }
    if (filterPatch.search !== undefined) setSearch(filterPatch.search);
    if (Array.isArray(filterPatch.warengruppe)) setSelectedWg(filterPatch.warengruppe);
    if (Array.isArray(filterPatch.materialart)) setSelectedMa(filterPatch.materialart);
    if (filterPatch.bestandsstatus) setBestandsstatus(filterPatch.bestandsstatus);
    setAdvFilters({
      lagerort: Array.isArray(filterPatch.lagerort) ? filterPatch.lagerort : DEFAULT_ADV.lagerort,
      beschaffungsart: Array.isArray(filterPatch.beschaffungsart) ? filterPatch.beschaffungsart : DEFAULT_ADV.beschaffungsart,
      dispomerkmal: Array.isArray(filterPatch.dispomerkmal) ? filterPatch.dispomerkmal : DEFAULT_ADV.dispomerkmal,
      bestand_min: filterPatch.bestand_min ?? DEFAULT_ADV.bestand_min,
      bestand_max: filterPatch.bestand_max ?? DEFAULT_ADV.bestand_max,
      offene_banf: filterPatch.offene_banf ?? DEFAULT_ADV.offene_banf,
      offene_bestellung: filterPatch.offene_bestellung ?? DEFAULT_ADV.offene_bestellung,
      handlungsbedarf: filterPatch.handlungsbedarf === true,
      stock_type: Array.isArray(filterPatch.stock_type) ? filterPatch.stock_type : DEFAULT_ADV.stock_type,
      vendor: Array.isArray(filterPatch.vendor) ? filterPatch.vendor : DEFAULT_ADV.vendor,
      ekgrp: Array.isArray(filterPatch.ekgrp) ? filterPatch.ekgrp : DEFAULT_ADV.ekgrp,
      sperrbestand: filterPatch.sperrbestand === true,
      qmbestand: filterPatch.qmbestand === true,
    });
    setMerkmale(Array.isArray(filterPatch.merkmale) ? filterPatch.merkmale : []);
    setKritischFilter(false);
    setPage(1);
  };

  const currentFiltersForSave = {
    werk: selectedWerk, search, warengruppe: selectedWg,
    materialart: selectedMa, bestandsstatus, ...advFilters, merkmale,
  };

  return (
    <div className={`flex-1 flex flex-col min-h-0 ${isDarkMode
      ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800'
      : 'bg-slate-50'
    }`}>

      {/* ---- Header ---- */}
      <div className="relative flex-shrink-0">
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gray-900/20 border-b border-white/10 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]'
          : 'bg-white border-b border-slate-200'
        }`} />
        <div className="relative max-w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Icon + Titel */}
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDarkMode
                ? 'bg-gradient-to-br from-slate-600/60 via-gray-600/50 to-slate-700/60 border border-white/20 shadow-[0_6px_24px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.3)]'
                : 'bg-brand/10'
              }`}>
                <Warehouse className={`w-5 h-5 ${isDarkMode ? 'text-white/90' : 'text-brand'}`} />
              </div>
              <div>
                <h1 className={`text-xl font-semibold ${isDarkMode
                  ? 'bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent'
                  : 'text-slate-900'
                }`}>{t('lm.navLagerbestand')}</h1>
                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
                  {t('lm.subtitle')}
                </p>
              </div>
            </div>

            {/* Werksauswahl + Export */}
            <div className="ml-auto flex items-center gap-2">
              {/* Kritisch-Chip (Drill-down aktiv) */}
              {kritischFilter && (
                <button
                  type="button"
                  onClick={() => { setKritischFilter(false); setPage(1); }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${isDarkMode
                    ? 'bg-amber-500/20 border-amber-500/30 text-amber-300 hover:bg-amber-500/30'
                    : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
                  }`}
                  title={t('common.remove')}
                >
                  {t('lm.kritischChip')}
                  <X className="w-3 h-3" />
                </button>
              )}

              {/* Export-Button */}
              <button
                type="button"
                onClick={handleExport}
                disabled={exportLoading || !selectedWerk}
                title={exportLoading ? t('lm.exportLoading') : t('lm.exportXlsx')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                  exportLoading || !selectedWerk
                    ? isDarkMode
                      ? 'bg-white/5 border-white/10 text-white/30 cursor-not-allowed'
                      : 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                    : isDarkMode
                      ? 'bg-brand/15 border-brand/30 text-blue-200 hover:bg-brand/25'
                      : 'bg-brand/8 border-brand/30 text-brand hover:bg-brand/15'
                }`}
              >
                {exportLoading
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Download className="w-3.5 h-3.5" />
                }
                <span className="hidden sm:inline">
                  {exportLoading ? t('lm.exportLoading') : t('lm.exportXlsx')}
                </span>
              </button>

              <WerkDropdown
                werke={werke}
                selectedWerk={selectedWerk}
                onWerkChange={handleWerkChange}
                loading={werkeLoading}
                werkOpen={werkOpen}
                setWerkOpen={setWerkOpen}
                werkRef={werkRef}
              />
            </div>
          </div>

          {/* ---- Suchleiste ---- */}
          <div className="mt-4 relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('lm.searchPlaceholder')}
              className={`w-full pl-10 pr-9 py-2.5 rounded-xl border text-sm outline-none transition-all ${isDarkMode
                ? 'bg-white/8 border-white/15 text-white placeholder-white/30 focus:border-brand/50 focus:bg-white/10'
                : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-brand/50 focus:ring-1 focus:ring-brand/20'
              }`}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className={`absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full transition-colors ${isDarkMode
                  ? 'text-white/40 hover:text-white/70'
                  : 'text-slate-400 hover:text-slate-600'
                }`}
                aria-label={t('common.close')}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ---- Schnellfilter + Erweiterte Filter ---- */}
      <div className="flex-shrink-0">
        <MaterialFilters
          warengruppen={filterOptions.warengruppen}
          materialarten={filterOptions.materialarten}
          selectedWg={selectedWg}
          selectedMa={selectedMa}
          bestandsstatus={bestandsstatus}
          onWgChange={(v) => { setSelectedWg(v); setPage(1); }}
          onMaChange={(v) => { setSelectedMa(v); setPage(1); }}
          onBestandsstatusChange={(v) => { setBestandsstatus(v); setPage(1); }}
          onReset={handleResetFilters}
          filterOptions={filterOptions}
          advFilters={advFilters}
          onAdvChange={handleAdvChange}
          user={user}
          werk={selectedWerk}
          merkmale={merkmale}
          onMerkmaleChange={setMerkmale}
        />
      </div>

      {/* ---- Gespeicherte Filter ---- */}
      <div className="flex-shrink-0">
        <SavedFilters
          user={user}
          currentFilters={currentFiltersForSave}
          onApplyFilter={handleApplySavedFilter}
          showSuccess={showSuccess}
          showError={showError}
        />
      </div>

      {/* ---- Filter merken (Auto-Restore pro User & Sicht) ---- */}
      <div className="flex-shrink-0 px-4 py-2 flex items-center">
        <AutoFilterToggle
          user={user}
          prefKey="lager-auto-filter"
          currentFilter={currentFiltersForSave}
          onRestore={handleApplySavedFilter}
        />
      </div>

      {/* ---- Trefferzeile ---- */}
      {!loading && !error && (
        <div className={`flex-shrink-0 px-4 sm:px-6 lg:px-8 py-2 text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-500'}`}>
          {t('lm.resultsCount', { count: new Intl.NumberFormat('de-DE').format(total) })}
        </div>
      )}

      {/* ---- Tabelle ---- */}
      <div className={`flex-1 overflow-auto ${isDarkMode ? '' : 'bg-white'}`}>
        <MaterialTable
          items={items}
          total={total}
          page={page}
          perPage={perPage}
          totalPages={totalPages}
          sort={sort}
          onSort={handleSort}
          onPageChange={setPage}
          onPerPageChange={handlePerPageChange}
          loading={loading}
          error={error}
          user={user}
          werk={selectedWerk}
          showSuccess={showSuccess}
          showError={showError}
          onCartUpdated={onCartUpdated}
          onNavigateToCreateBanf={onNavigateToCreateBanf}
        />
      </div>
    </div>
  );
};

export default LagerView;
